// Ask about this call: the pure rules behind the panel beside the transcript (docs/api/ASK.md).
// The server answers from the call's transcript only, through the provider the operator configured; this file
// decides when the panel exists, reads the reply defensively and keeps the per-call history.
import type { AskAnswer, Capability, Config } from '@bcio-voice/contracts';
import type { ApiClient } from '../../lib/api.ts';
import { usable } from '../../lib/capabilities.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';

export const ASK_CAPABILITY = 'transcript.ask';
export const ASK_MAX_LENGTH = 500;
/** History kept per call while the page is open; older entries drop off the end. */
export const ASK_HISTORY_LIMIT = 20;
export const QUICK_QUESTIONS = ['What did they ask for?', 'Any dates, amounts or names?', 'What should I do next?'] as const;

export function askCapability(config: Config | null): Capability | null {
  return config?.capabilities[ASK_CAPABILITY] ?? null;
}

/**
 * The panel exists only when the server reports the feature usable, the person can read transcripts and their
 * media access is settled. Otherwise it renders nothing: no empty panel and no teaser for an unconfigured server.
 */
export function askVisible(cap: Capability | null, permissions: readonly string[], mediaReady: boolean): boolean {
  return mediaReady && permissions.includes('transcripts:read') && usable(cap);
}

/** One line, at most 500 characters; an empty result means there is nothing to ask. */
export function cleanQuestion(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, ASK_MAX_LENGTH);
}

/** Escape clears a typed question (and does nothing on an empty field, so it never swallows other Escape uses). */
export function escapeClears(key: string, draft: string): boolean {
  return key === 'Escape' && draft !== '';
}

export type AskEntry =
  | { id: string; question: string; status: 'loading' }
  | { id: string; question: string; status: 'answered'; answer: AskAnswer }
  | { id: string; question: string; status: 'error'; error: ErrorView };

export type AskAction =
  | { type: 'asked'; id: string; question: string }
  | { type: 'answered'; id: string; answer: AskAnswer }
  | { type: 'failed'; id: string; error: ErrorView }
  | { type: 'retried'; id: string };

/** Newest first. A retry moves the entry back to loading in place; nothing else reorders. */
export function askReducer(entries: readonly AskEntry[], action: AskAction): AskEntry[] {
  switch (action.type) {
    case 'asked':
      return [{ id: action.id, question: action.question, status: 'loading' as const }, ...entries].slice(0, ASK_HISTORY_LIMIT);
    case 'answered':
      return entries.map(e => (e.id === action.id ? { id: e.id, question: e.question, status: 'answered' as const, answer: action.answer } : e));
    case 'failed':
      return entries.map(e => (e.id === action.id ? { id: e.id, question: e.question, status: 'error' as const, error: action.error } : e));
    case 'retried':
      return entries.map(e => (e.id === action.id ? { id: e.id, question: e.question, status: 'loading' as const } : e));
  }
}

const STATES = new Set(['answered', 'no_answer']);
const PROVIDERS = new Set(['anthropic', 'openai_compatible']);
const count = (value: unknown) => typeof value === 'number' && Number.isInteger(value) && value >= 0;

/** The `{data}` envelope from POST /calls/{id}/ask, or null when it is not a reply this version can read. */
export function readAskAnswer(body: unknown): AskAnswer | null {
  const data = typeof body === 'object' && body !== null ? (body as { data?: unknown }).data : undefined;
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (typeof d.answer !== 'string' || !d.answer.trim() || d.answer.length > 2000) return null;
  if (typeof d.model !== 'string' || !d.model || typeof d.provider !== 'string' || !PROVIDERS.has(d.provider)) return null;
  if (!count(d.latency_ms) || !count(d.segments_used) || typeof d.provisional !== 'boolean' || typeof d.state !== 'string' || !STATES.has(d.state)) return null;
  return {
    answer: d.answer,
    provider: d.provider as AskAnswer['provider'],
    model: d.model,
    latency_ms: d.latency_ms as number,
    segments_used: d.segments_used as number,
    provisional: d.provisional,
    truncated: d.truncated === true,
    state: d.state as AskAnswer['state'],
  };
}

/** 1.8 under ten seconds, whole seconds above. */
export function seconds(ms: number): string {
  return ms < 10000 ? (Math.max(0, ms) / 1000).toFixed(1) : String(Math.round(ms / 1000));
}

export function answeredLine(answer: Pick<AskAnswer, 'model' | 'latency_ms'>): string {
  return `Answered by ${answer.model} in ${seconds(answer.latency_ms)} s`;
}

const view = (code: string, title: string, detail: string, error: unknown): ErrorView => {
  const base = describeError(error, title);
  return { ...base, code, title, detail };
};

/** Plain words for the ask-specific codes; everything else uses the shared copy table. */
export function askErrorView(error: unknown): ErrorView {
  const code = describeError(error).code;
  switch (code) {
    case 'ask_timeout':
      return view(code, 'The answer took too long', 'Try again. If it keeps happening, ask a shorter question.', error);
    case 'ask_provider_error':
      return view(code, 'The answer service could not answer', 'Try again in a moment. Your transcript is unchanged.', error);
    case 'ask_unconfigured':
      return view(code, 'Questions about calls are not set up', 'An administrator needs to finish setting up answers on this server.', error);
    case 'transcript_empty':
      return view(code, 'There is no transcript text yet', 'Ask again once the transcript shows what was said.', error);
    case 'rate_limited':
      return view(code, 'Too many questions in a minute', 'Wait a moment, then ask again.', error);
    default:
      return describeError(error, 'Could not get an answer');
  }
}

export const UNREADABLE_ANSWER: ErrorView = {
  title: 'The server sent an answer this version cannot read',
  detail: 'Reload the page, then ask again.',
  code: 'invalid_response',
  requestId: null,
  retryable: true,
};

/** What the screen reader hears when a reply lands. Never the answer itself (it is drawn from the transcript). */
export function announcement(entry: AskEntry): string | null {
  if (entry.status === 'answered') return entry.answer.state === 'no_answer' ? 'No answer found in the transcript.' : 'Answer ready.';
  if (entry.status === 'error') return 'Could not get an answer.';
  return null;
}

/** POST /calls/{id}/ask with the question only; the server reads the transcript itself. */
export class AskClient {
  constructor(private readonly api: Pick<ApiClient, 'send'>) {}

  ask(callId: string, question: string, signal?: AbortSignal): Promise<unknown> {
    return this.api.send('POST', `/calls/${encodeURIComponent(callId)}/ask`, { question }, signal ? { signal } : {});
  }
}
